import { createEffect, createSignal, onCleanup, onMount } from 'solid-js'

import { getOpenHabitTaskCount, habits } from '../state/habitsStore'
import { updateAppBadge } from './appBadge'

const HABIT_BADGE_REFRESH_MS = 60 * 1000

/**
 * Wires the OS app badge to the current habit due count and foreground/background events.
 *
 * @param handleForeground - Called when the app resumes or becomes visible.
 */
export function useHabitAppBadge(handleForeground: () => void) {
   const [habitBadgeTime, setHabitBadgeTime] = createSignal(Date.now())
   let lastWrittenBadgeCount: number | null = null

   createEffect(() => {
      const openTaskCount = getOpenHabitTaskCount(habits(), habitBadgeTime())
      // Hidden contexts may hold stale habit state (background sync is paused),
      // so only the visible context is allowed to write the shared OS badge.
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
         return
      }
      if (openTaskCount === lastWrittenBadgeCount) {
         return
      }
      lastWrittenBadgeCount = openTaskCount
      updateAppBadge(openTaskCount)
   })

   onMount(() => {
      const habitBadgeTimerId = window.setInterval(() => {
         setHabitBadgeTime(Date.now())
      }, HABIT_BADGE_REFRESH_MS)

      /**
       * Refreshes the badge and notifies the foreground handler. Another
       * context may have changed the badge while this one was hidden, so the
       * dedupe cache is reset to force a rewrite even for an unchanged count.
       */
      const refreshBadgeAndForeground = () => {
         lastWrittenBadgeCount = null
         setHabitBadgeTime(Date.now())
         handleForeground()
      }
      /**
       * Refreshes the badge when the document becomes visible.
       */
      const handleVisibilityChange = () => {
         if (document.visibilityState === 'visible') {
            refreshBadgeAndForeground()
         }
      }

      window.addEventListener('focus', refreshBadgeAndForeground)
      window.addEventListener('pageshow', refreshBadgeAndForeground)
      document.addEventListener('visibilitychange', handleVisibilityChange)
      document.addEventListener('resume', refreshBadgeAndForeground)

      onCleanup(() => {
         window.clearInterval(habitBadgeTimerId)
         window.removeEventListener('focus', refreshBadgeAndForeground)
         window.removeEventListener('pageshow', refreshBadgeAndForeground)
         document.removeEventListener('visibilitychange', handleVisibilityChange)
         document.removeEventListener('resume', refreshBadgeAndForeground)
      })
   })
}
